import { useCallback, useState, useEffect } from 'react';
import { notification, Input, Button, Space, Modal, Select, Tooltip, Tag } from 'antd';
import { PlusOutlined, KeyOutlined, CopyOutlined, FormOutlined, CloudDownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import "./VPN.scss";
import service from '../../services/Service.js';
import { getTokenInfo, hasPrivilege } from '../../api/token.js';
import { noWhiteSpacesValidation, RUTSerialNumberValidation, RUTUuidValidation, usernameValidation, uuidValidateV4 } from "../../utils/formValidation.jsx";
import { withLocale } from '../../l10n/index.jsx';
import DataTable from '../DataTable/DataTable.jsx';
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { formatDateTime } from '../../utils/dates';
import { REFRESH_INTERVAL_MILISECONDS } from '../../utils/constants.js';
import DeleteButton from '../DeleteButton/DeleteButton.jsx';
import withNavigation from '../hooks/withNavigation.jsx';
import { useGetModelsLookupQuery, useGetTenantsLookupQuery } from '../../services/lookupApi.js';

function VPN ({lang, navigate}) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
    editPassword: false,
    showModal: false,
    isNew: false,
    isSubmitting: false,
  })

  const [data, setData] = useState({
    vpnList: [],
    selected: {},
  });

  const [form, setForm] = useState({
    username: null,
    password: null,
    tenant: null,
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: models = [] } = useGetModelsLookupQuery();
  const { data: tenants = [] } = useGetTenantsLookupQuery();

  // ===== 3. DERIVADOS (Computados) =====
  const botonDisabled = !data.selected || Object.keys(data.selected).length === 0;
  const modelFilters = models.map(m => ({ text: m.name, value: m.name }));
  const tenantFilters = tenants.map(t => ({ text: t.name, value: t.uuid }));

  // ===== 4. HELPERS (Para actualizar estado) =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  

  // ===== 5. FUNCIONES PRINCIPALES =====
  const fetch = useCallback(async () => {
    await service.get('vpn', { status: true })
      .then(res => {
        let vpnList;
        if (!res || res.data.ok === false) {
          notification["error"]({
            message: lang.handle(res.data.message),
          });
          vpnList = [];
        } else {
          vpnList = res.data;
        }

        updateData('vpnList', vpnList);
      });
  }, [lang]);


  // ===== 6. HANDLERS =====
  const createVPN = async () => {
    generateRandomPassword();
    updateUI('isNew', true);
    updateUI('showModal', true);
  }

  const editVPN = () => {
    if (data.selected.type === 'user') {
      updateUI('loading', true);
      navigate(`/users/edit/${data.selected.username}`);
      return;
    }

    updateForm('username', data.selected.username);
    updateForm('tenant', data.selected.tenant);
    updateUI('showModal', true);
  }

  const changePassword = () => {
    if (data.selected.type === 'user') {
      updateUI('loading', true);
      navigate(`/users/changepassword/${data.selected.username}`);
      return;
    }

    generateRandomPassword();
    updateForm('username', data.selected.username);
    updateUI('editPassword', true);
    updateUI('showModal', true);
  }

  const deleteVPN = async () => {
    await service
      .del(`vpn/${data.selected.username}`)
      .then((res) => {
        notification["success"]({
          message: lang.handle(res.data.message),
        });
        updateData('selected', {});
        fetch();
      })
      .catch((err) => {
        console.log("Error devuelto: " + err);
      });
  }

  const closeAllModals = () => {
    updateData('selected', {});
    setUI({
      loading: false,
      showModal: false,
      editPassword: false,
      isNew: false,
      isSubmitting: false
    });
    setForm({
      username: null,
      password: null,
      tenant: null
    });
  };

  const validate = () => {
    if (!noWhiteSpacesValidation(form.username) || (!uuidValidateV4(form.username) && !RUTUuidValidation(form.username) && !RUTSerialNumberValidation(form.username) && !usernameValidation(form.username))) {
      notification["error"]({
        message: lang.handle('¡Usuario erróneo!'),
      });
      return false;
    }
    if (ui.editPassword && !form.password) {
      notification["error"]({
        message: lang.handle('¡Contraseña no válida!'),
      });
      return false;
    }
    if (!ui.editPassword && !form.tenant) {
      notification["error"]({
        message: lang.handle('¡Debe seleccionar una organización!'),
      });
      return false;
    }

    return true;
  }

  const onSubmit = async () => {
    if (!validate() || ui.isSubmitting) {
      return;
    }
    
    updateUI('isSubmitting', true);
    
    if (ui.isNew) {
      await service
        .post("vpn", { 
          username: form.username, 
          password: form.password, 
          tenant: form.tenant 
        })
        .then((res) => {
          if (res.data.ok === false) {
            notification["error"]({
              message: lang.handle(res.data.message),
            });
            updateUI('isSubmitting', false);
            return;
          } else {
            notification["success"]({
              message: lang.handle(res.data.message),
            });
            closeAllModals();
          }
        })
    } else {
      const requestData = ui.editPassword 
        ? { password: form.password } 
        : { username: form.username, tenant: form.tenant };
      await service
        .put("vpn/" + data.selected.username, requestData)
        .then((res) => {
          if (res.data.ok === false) {
            notification["error"]({
              message: lang.handle(res.data.message),
            });
            updateUI('isSubmitting', false);
            return;
          } else {
            notification["success"]({
              message: lang.handle('VPN actualizada correctamente'),
            });
            closeAllModals();
          }
        })
    }
    fetch();
  };

  const onInputChange = (e) => {
    updateForm('username', e.target.value);
  }

  const onInputPasswordChange = (e) => {
    updateForm('password', e.target.value);
  }

  const onInputTenantChange = (value) => {
    updateForm('tenant', value);
  }

  const generateRandomPassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    updateForm('password', result);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(form.password).then(() => {
      notification["success"]({
        message: lang.handle('Contraseña copiada al portapapeles'),
      });
    }).catch(err => {
      console.log(err)
      notification["error"]({
        message: lang.handle('Fallo al copiar la contraseña'),
      });
    });
  };

  const downloadVPNFile = async () => {
    await service
      .post(`vpn/${getTokenInfo().uuid}`)
      .then(async (res) => {
        if (res) {
          await service.download(`vpn/${getTokenInfo().uuid}/download`, import.meta.env.VITE_VPN_FILENAME);
        }
      });
  }

  const handleOpenOldVPN = () => {
    const URL_VPN = import.meta.env.VITE_VPN_URL;
    window.open(URL_VPN, "_blank");
  };


  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loading', true);
    fetch().then(() => {
      updateUI('loading', false);
    });
  }, [fetch]);

  useEffect(() => {
    const id = setInterval(fetch, REFRESH_INTERVAL_MILISECONDS);

    return () => {
      clearInterval(id);
    };
  }, [fetch]);


  // ===== 8. CONFIG (columnas, etc) =====
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      if (selectedRows.length === 0) {
        updateData('selected', null);
      } else {
        updateData('selected', selectedRows[0]);
      } 
    }
  };

  const options = [
    {
      label: 'KICONEX',
      value: '90be8c8a-f462-4a3e-afcf-d8f34094eaa8' //0
    },
    {
      label: 'INTARCON',
      value: '54da349f-b026-4e4c-a571-2efe9eb644ed' //11
    },
    {
      label: 'KEYTER',
      value: 'edf19151-34fa-4385-878a-b725d8450763' //12
    },
    {
      label: 'GENAQ',
      value: '504549ae-bee1-4f5f-abc1-7c4fbd6b0c20' //13
    },
    {
      label: 'SEGUAS',
      value: 'b0c3d8f6-a126-4c74-aff9-e1f77e95d7b9' //14
    },
    {
      label: 'FMGRUPO',
      value: 'c5557dfe-7eba-455a-b224-dac06dea3cd9' //15
    },
    {
      label: 'PUREAIR AG',
      value: '485180e4-0098-49d5-b1a9-80afe2efbef1' //16
    },
    {
      label: 'PROTOTIPOS',
      value: '52af8043-615a-436d-83a9-ca42ff217e62' //17
    },
    {
      label: 'SERVOCLIMA',
      value: 'a1f192e3-c4cc-46be-ba01-6e3aa2a4d5f2' //18
    },
  ]

  const columns = [
    {
      title: lang.handle("Instalación"),
      dataIndex: 'device_name',
      ...GetColumnLiteralSearchProps('device_name', lang)
    },
    {
      title: lang.handle("Modelo"),
      dataIndex: 'model_name',
      filters: modelFilters,
      onFilter: (value, record) => record.model_name === value,
    },
    {
      title: lang.handle("Usuario"),
      dataIndex: 'username',
      ...GetColumnLiteralSearchProps('username', lang)
    },
    {
      title: lang.handle('Organización'),
      dataIndex: 'tenant',
      filters: tenantFilters,
      onFilter: (value, record) => record.tenant === value,
      render: (data) => { 
          const tenantObj = tenantFilters.find(e => e.value === data);
          return tenantObj ? tenantObj.text : '';
      }
    },
    {
      title: 'IP',
      dataIndex: 'framedipaddress',
      ...GetColumnLiteralSearchProps('framedipaddress', lang)
    },
    {
      title: lang.handle('Estado'),
      dataIndex: 'status',
      filters: [
        { text: lang.handle('Conectada'), value: true },
        { text: lang.handle('Desconectada'), value: false }
      ],
      onFilter: (value, record) => record.status.detected === value,
      render: function (data) {
        const color = data.detected ? 'green' : 'red';
        const message = data.detected ? lang.handle('Conectada') : lang.handle('Desconectada');
        const date = formatDateTime(data.lastchange);
        return <Tooltip title={date !== '' ? date : lang.handle('Nunca')}> <Tag color={color}> {message} </Tag> </Tooltip>;
      }
    }
  ];
  if (hasPrivilege('vpn_admin')) {
    columns.push(
      {
        title: lang.handle('Tipo'),
        dataIndex: 'type',
        align: "center",
        filters: [{ text: lang.handle('Usuario'), value: 'user' }, { text: lang.handle('Dispositivo'), value: 'device' }],
        onFilter: (value, record) => { const check = record.type ?? null; return check === value },
        defaultFilteredValue: ['device'],
        render: function (data) {
          if (data === null) {
            return null;
          }
          return <p> {data === 'user' ? lang.handle('Usuario') : lang.handle('Dispositivo')} </p>;
        },
      }
    );
  }


  // ===== 9. RENDER =====
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          {hasPrivilege('vpn_admin') &&
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={createVPN}
            >
              {lang.handle('Nueva conexión VPN')}
            </Button>
          }
        </div>
        <div>
          {hasPrivilege('vpn_view_old') &&
            <Button
              type="default"
              onClick={handleOpenOldVPN}
            >
              {lang.handle('VPN Antigua')}
            </Button>
          }
        </div>
        <div>
          {hasPrivilege('vpn_view') &&
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              onClick={downloadVPNFile}
            >
              {lang.handle('Descargar fichero de configuración')}
            </Button>
          }
        </div>
      </div>
      <DataTable
        columns={columns}
        dataSource={data.vpnList}
        rowKey="username"
        rowSelection={{
          type: "radio",
          selectedRowKeys: data.selected?.username ? [data.selected.username] : [],
          ...rowSelection
        }}
        loading={ui.loading}
        scroll={null}
        onChange={() => {
          updateData('selected', {});
        }}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('vpn_admin') && <Button
            type="primary"
            icon={<FormOutlined />}
            disabled={botonDisabled}
            onClick={editVPN}
          >
            {lang.handle('Editar')}
          </Button>}
          {hasPrivilege('vpn_admin') && <Button
            type="primary"
            icon={<FormOutlined />}
            disabled={botonDisabled}
            onClick={changePassword}
          >
            {lang.handle('Regenerar contraseña')}
          </Button>}

          {hasPrivilege('manufacture_delete') &&
            <DeleteButton
              onEvent={deleteVPN}
              buttonDisabled={botonDisabled}
            />}
        </Space>
      </div>

      <Modal
        title={lang.handle('Contraseña VPN')}
        onCancel={closeAllModals}
        cancelText={lang.handle('Cerrar')}
        onOk={onSubmit}
        okText={lang.handle('Guardar')}
        icon={<KeyOutlined />}
        open={ui.showModal}
        width={600}
        okButtonProps={{ loading: ui.isSubmitting, disabled: ui.isSubmitting }}
      >
        {lang.handle('Copie la contraseña antes de guardar, no volverá a ser visible')}
        <form onSubmit={onSubmit} className="p-3">
          <div className="form-group row">
            <label htmlFor="username"> {lang.handle('Usuario')}
              <Tooltip placement="top" title={lang.handle('Formatos correctos: UUID, RUT-UUID, RUT-SN. También cadena de texto minúscula de entre 6 y 16 caracteres.')}>
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </label>
            <Input name='username' value={form.username} className='col' onChange={onInputChange} readOnly={ui.editPassword} />
          </div>

          {(ui.editPassword || ui.isNew) && <div className="form-group row">
            <label htmlFor="password"> {lang.handle('Contraseña')} </label>
            <Input.Password name='password' value={form.password} onChange={onInputPasswordChange} className='col' />
            <Button onClick={copyToClipboard} disabled={form.password === null} className='col-2'><CopyOutlined /></Button>
          </div>}

          {!ui.editPassword && <div className="form-group row">
            <label htmlFor="tenant"> {lang.handle('Organización')} </label>
            <Select
              className="form-control"
              onChange={onInputTenantChange}
              value={form.tenant}
              options={options}
            />
          </div>}
        </form>

      </Modal>
    </div>
  );
}

export default withLocale(withNavigation(VPN));
